// Story 7.12 (ADO-911), AC1/AC2 — the RecommendationRail: a horizontal-scroll
// carousel of product cards (no carousel/slider primitive exists in the app, D-5).
//
// Story 7.12 Iteration 2 (ADO-911), DR-4 + the 2026-07-29 design refresh — this
// rail's permanent home is the Product Detail Page, as the "Often purchased
// together" carousel (the interim card-hosted `RecommendationsSection` is
// retired). Two decisions from that refresh, both confirmed with Marika:
//
//   1. TILES ARE THE SHARED GRID `ProductCard`. The updated Figma draws the
//      carousel as `Product Card - Grid View` instances (308×469), not bespoke
//      tiles — so the rail mounts the real card rather than a look-alike, with
//      `hideRrpAndMargin` (D-1: no RRP or margin on a tile; the footer's unit
//      price still shows), inheriting the card's tested chrome: badges, favourite
//      heart, stock pill, the unit price and the one-click Add (the grid card
//      has no Stock card button or stock teaser, ADO-2011). It also retires
//      the Iteration-1 bespoke `RailTile`/`RailAddButton`, so there is now ONE
//      product-card implementation in the app instead of two that could drift.
//   2. CAROUSEL CHROME MATCHES THE REFRESHED DESIGN: prev/next arrows sit
//      OVERLAID on the track edges (not in the header), a scroll-progress thumb
//      replaces the pager dots, and the "See all N" affordance is gone.
//
// a11y (NFR24–26, Story 7.7 44px floor): the rail is a labelled <section> (the
// accessible name gives it the implicit role="region"), the tile list is
// focusable + arrow-key scrollable, the arrows carry disambiguating aria-labels
// and are hidden from AT when they cannot move (the list itself is the
// keyboard-accessible path), and the progress thumb is decorative
// (`aria-hidden`) because it reports what the scroll container already exposes.
// Scroll POSITION and geometry ride Playwright — happy-dom has no layout engine
// (the standing 7.7 posture).
//
// It stays a pure presentational component (title + items in): its HOST owns the
// lazy fetch and the client-side availability composition.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../../design-system';
import type { RecommendedProduct } from '../../api/recommendations.client';
import { toProductSummary } from '../../recommendations/toProductSummary';
import { ProductCard } from './ProductCard';
import styles from './RecommendationRail.module.css';

export interface RecommendationRailProps {
  /** The rail heading — names the region for assistive tech. */
  readonly title: string;
  /** The tiles to show (already capped + account-type-filtered + availability-
   *  composed upstream). Non-empty — the host renders no rail for an empty list (AC3). */
  readonly items: readonly RecommendedProduct[];
  /** Heading level for the rail title. The PDP mounts the rail as a top-level
   *  page section (h2); the default keeps the smaller in-card heading. */
  readonly headingLevel?: 2 | 3 | 4;
}

/** How far one prev/next press scrolls (px). A no-op in happy-dom (no layout). */
const SCROLL_STEP = 324;

export function RecommendationRail({ title, items, headingLevel = 4 }: RecommendationRailProps) {
  const scrollerRef = useRef<HTMLUListElement>(null);
  // Scroll-progress thumb (design `Scroll Indicator` 1271:7409): the fraction of
  // the track that is visible, and how far along it the viewport sits. Both are
  // 0 under happy-dom (no layout engine) — the thumb then spans the full width,
  // which is the correct "nothing to scroll" presentation.
  const [progress, setProgress] = useState({ size: 1, offset: 0 });

  const scrollByStep = (direction: -1 | 1) => {
    // `scrollBy` is absent under happy-dom — guard so the unit tests exercising
    // the controls don't crash; real scrolling rides Playwright.
    scrollerRef.current?.scrollBy?.({ left: direction * SCROLL_STEP, behavior: 'smooth' });
  };

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (el === null) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    if (scrollWidth <= 0 || clientWidth <= 0 || scrollWidth <= clientWidth) {
      setProgress({ size: 1, offset: 0 });
      return;
    }
    setProgress({
      size: clientWidth / scrollWidth,
      // Clamped so a rubber-band overscroll can't push the thumb out of its track.
      offset: Math.min(1, Math.max(0, scrollLeft / scrollWidth)),
    });
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el === null) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    // The thumb must also re-measure when the rail is resized (a viewport change
    // alters how much of the track is visible). ResizeObserver is absent in
    // happy-dom, hence the guard.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [measure, items.length]);

  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  const atStart = progress.offset <= 0.001;
  const atEnd = progress.offset + progress.size >= 0.999;

  return (
    <section className={styles.rail} aria-label={title}>
      <Heading className={styles.title}>{title}</Heading>

      <div className={styles.track}>
        <button
          type="button"
          className={`${styles.control} ${styles.controlPrev}`}
          aria-label={`Scroll ${title} left`}
          disabled={atStart}
          onClick={() => scrollByStep(-1)}
        >
          <Icon name="caretLeft" />
        </button>

        {/* The scroller is a focusable, arrow-key-scrollable list (a WAI-ARIA
            scroll region): tabIndex makes it keyboard-focusable and onKeyDown
            drives the horizontal scroll — the intended interaction on a scroll
            container, not a noninteractive misuse. eslint's heuristics don't
            model a scroll region, so both rules are disabled for this element. */}
        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <ul
          ref={scrollerRef}
          className={styles.scroller}
          tabIndex={0}
          aria-label={`${title} products`}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              scrollByStep(1);
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              scrollByStep(-1);
            }
          }}
        >
          {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
          {items.map((item) => (
            <li key={item.sku} className={styles.tile}>
              {/* ADO-911 design refresh — this rail is the PDP "Often purchased
                  together" carousel, so a tile add opens the full MODAL, matching
                  the PDP hero Add rather than the listing toast. */}
              <ProductCard
                product={toProductSummary(item)}
                hideRrpAndMargin
                partialRow
                upsellVariant="modal"
                nameRow="two-lines"
              />
            </li>
          ))}
        </ul>

        <button
          type="button"
          className={`${styles.control} ${styles.controlNext}`}
          aria-label={`Scroll ${title} right`}
          disabled={atEnd}
          onClick={() => scrollByStep(1)}
        >
          <Icon name="caretRight" />
        </button>
      </div>

      {/* Scroll-progress indicator (design 1271:7409). Purely a reflection of the
          scroller's own state, which AT already exposes through the scroll
          container — so it is decorative and hidden from the accessibility tree
          rather than duplicated as a widget. */}
      <div className={styles.progressTrack} aria-hidden="true">
        <div
          className={styles.progressThumb}
          style={{
            width: `${Math.max(8, progress.size * 100)}%`,
            marginInlineStart: `${progress.offset * 100}%`,
          }}
        />
      </div>
    </section>
  );
}
